import React from "react";
import api from "../services/api";

const DEFAULT_CURRENCY = {
  code: "INR",
  locale: "en-IN",
};

const STORAGE_KEY = "siteCurrency";

export function getCachedSiteCurrency() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_CURRENCY };
    const parsed = JSON.parse(raw) || {};
    const code = String(parsed.code || DEFAULT_CURRENCY.code).trim().toUpperCase();
    const locale = String(parsed.locale || DEFAULT_CURRENCY.locale).trim();
    if (!code || !locale) return { ...DEFAULT_CURRENCY };
    return { code, locale };
  } catch {
    return { ...DEFAULT_CURRENCY };
  }
}

export function setCachedSiteCurrency(currency) {
  try {
    const code = String(currency?.code || DEFAULT_CURRENCY.code).trim().toUpperCase();
    const locale = String(currency?.locale || DEFAULT_CURRENCY.locale).trim();
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ code, locale }));
    window.dispatchEvent(new Event("site-currency-changed"));
  } catch {
  }
}

export async function refreshSiteCurrency() {
  try {
    let res;
    try {
      res = await api.get("/settings/currency");
    } catch (err) {
      if (err?.response?.status === 404) {
        res = await api.get("/site-settings/currency");
      } else {
        throw err;
      }
    }

    const c = res?.data?.currency;
    if (!c || !c.code || !c.locale) return getCachedSiteCurrency();
    const next = {
      code: String(c.code).trim().toUpperCase(),
      locale: String(c.locale).trim(),
    };
    setCachedSiteCurrency(next);
    return next;
  } catch {
    return getCachedSiteCurrency();
  }
}

export function useSiteCurrency() {
  const [currency, setCurrency] = React.useState(() => getCachedSiteCurrency());

  React.useEffect(() => {
    const onChange = () => setCurrency(getCachedSiteCurrency());
    window.addEventListener("site-currency-changed", onChange);
    return () => window.removeEventListener("site-currency-changed", onChange);
  }, []);

  React.useEffect(() => {
    refreshSiteCurrency().catch(() => {});
  }, []);

  return currency;
}

export function formatMoney(value, currency) {
  const num = Number(value);
  if (Number.isNaN(num)) return "-";

  const c = currency || getCachedSiteCurrency();
  try {
    return new Intl.NumberFormat(c.locale, {
      style: "currency",
      currency: c.code,
      minimumFractionDigits: 2,
    }).format(num);
  } catch {
    return new Intl.NumberFormat(DEFAULT_CURRENCY.locale, {
      style: "currency",
      currency: DEFAULT_CURRENCY.code,
      minimumFractionDigits: 2,
    }).format(num);
  }
}
